<template>
  <transition name="fade">
    <div v-show="widget" :class="{ fullscreen: zoomIn } " v-resize:debounce.300="refreshChart" ref="chartContainer">
      <div style="position: absolute; width: 0; height: 0;">
        <!--https://jsfiddle.net/ezanker/0nmfz6cd/5/-->
        <!--http://iros.github.io/patternfills/sample_svg.html-->
        <svg height="10" width="10" xmlns="http://www.w3.org/2000/svg" version="1.1">
          <defs>
            <pattern id="circles-0" width="4" patternUnits="userSpaceOnUse" height="4"
                     patternTransform="rotate(45 0 0)">
              <line x1="0" y1="0" x2="0" y2="2" style="stroke:black; stroke-width:1"/>
            </pattern>
            <pattern id="diagonal-stripe-0" patternUnits="userSpaceOnUse" width="10" height="10"
                     patternTransform="rotate(45 0 0)">
              <line x1="0" y1="0" x2="0" y2="10" style="stroke:black; stroke-width:1"/>
            </pattern>
            <pattern id="diagonal-stripe-1" patternUnits="userSpaceOnUse" width="10" height="10">
              <image
                xlink:href="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPSd3aGl0ZScvPgogIDxwYXRoIGQ9J00tMSwxIGwyLC0yCiAgICAgICAgICAgTTAsMTAgbDEwLC0xMAogICAgICAgICAgIE05LDExIGwyLC0yJyBzdHJva2U9J2JsYWNrJyBzdHJva2Utd2lkdGg9JzEnLz4KPC9zdmc+Cg=="
                x="0" y="0" width="10" height="10"></image>
            </pattern>
            <pattern id="diagonal-stripe-2" patternUnits="userSpaceOnUse" width="10" height="10">
              <image
                xlink:href="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPSd3aGl0ZScvPgogIDxwYXRoIGQ9J00tMSwxIGwyLC0yCiAgICAgICAgICAgTTAsMTAgbDEwLC0xMAogICAgICAgICAgIE05LDExIGwyLC0yJyBzdHJva2U9J2JsYWNrJyBzdHJva2Utd2lkdGg9JzInLz4KPC9zdmc+"
                x="0" y="0" width="10" height="10"></image>
            </pattern>
            <pattern id="circles-1" patternUnits="userSpaceOnUse" width="10" height="10">
              <image
                xlink:href="data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScxMCcgaGVpZ2h0PScxMCc+CiAgPHJlY3Qgd2lkdGg9JzEwJyBoZWlnaHQ9JzEwJyBmaWxsPSJ3aGl0ZSIgLz4KICA8Y2lyY2xlIGN4PSIxIiBjeT0iMSIgcj0iMSIgZmlsbD0iYmxhY2siLz4KPC9zdmc+"
                x="0" y="0" width="10" height="10"></image>
            </pattern>
          </defs>
        </svg>
      </div>
      <div v-if="!skipButtons && !tableMode" :class="{ 'fullscreen-button': zoomIn, 'actions': true }"
           class="ui basic tiny buttons">
        <template v-if="actions.length">
          <button :key="`action-buttons-${index}`" v-for="(button, index) in actions" v-show="!printMode"
                  class="ui icon button" @click.prevent="action.onClick" :title="action.title">
            <i :class="[action.icon, 'icon']"></i>
          </button>
        </template>
        <button v-show="!printMode" class="ui icon button" @click.prevent="exportAsExcel"
                title="Export to Excel">
          <i class="download icon"></i>
        </button>
        <button v-show="!printMode" class="ui icon button" @click.prevent="zoom" title="Zoom">
          <i :class="[{ expand:!zoomIn }, { compress:zoomIn }, 'icon']"></i>
        </button>
      </div>
      <div v-show="!tableMode && !printMode && !zoomIn" ref="widget"></div>
      <div v-show="tableMode && !printMode" ref="table"></div>
      <img v-if="!tableMode && printMode" class="ui fluid image" :src="image"/>
      <transition name="fade">
        <img v-if="!printMode && zoomActive" class="ui fluid image" :src="image"/>
      </transition>
      <br v-if="tableMode"/>
    </div>
  </transition>
</template>

<script>
import { momentMixin } from '@/modules/neodash/mixins';

import { PRINT_GET_PRINT_MODE, PRINT_GET_TABLE_MODE } from '@/store/modules/print/types';
import { DISABLE_LOADING, ENABLE_LOADING } from '@/store/types';
import _ from 'lodash';
import resize from 'vue-resize-directive';
import { mapActions, mapGetters } from 'vuex';
import { LabelVisualOverlapping } from './kendo-label-overlapping/';

export default {
  directives: {
    resize
  },

  props: {
    title: {
      type: String
    },
    type: {
      type: String
    },
    stack: [Boolean, Object],
    category: {
      type: Object
    },
    series: {
      type: Array
    },
    seriesClick: {
      type: Function,
      default: () => {}
    },
    noOverlapping: {
      type: Boolean
    },
    label: {
      type: Object
    },
    legend: {
      type: Object,
      default: () => ({})
    },
    dataSource: {
      type: Array
    },
    format: {
      type: String,
      default: '{0:n2}'
    },
    gap: {
      type: Number,
      default: 0.5
    },
    height: {
      type: Number,
      default: 340
    },
    colors: {
      type: Array,
      default: () => ['#1ABC9C', '#3498DB', '#9B59B6', '#34495E', '#E74C3C', '#E67E22', '#F1C40F', '#ECF0F1']
    },
    fontSize: {
      type: Number,
      default: 11
    },
    valueAxis: {
      type: [Object, Array],
      default: () => ({})
    },
    categoryAxis: {
      type: Object,
      default: () => ({})
    },
    chartArea: {
      type: Object,
      default: () => ({})
    },
    printSize: {
      type: Object,
      default: () => ({
        height: 340,
        width: window.outerWidth
      })
    },
    skipButtons: {
      type: Boolean,
      default: false
    },
    actions: {
      type: Array,
      default: () => []
    },
    renderAs: {
      type: String,
      default: 'canvas'
    }
  },

  data () {
    return {
      config: undefined,
      labelVisual: this.labelVisual,
      image: undefined,
      widget: undefined,
      table: undefined,
      zoomIn: false,
      zoomActive: false,
      isGeneratingImage: false,
      generatingImageTimeout: undefined,
      updatingOptions: false,
      updatingOptionsTimeout: undefined
    };
  },

  mixins: [momentMixin],

  computed: {
    ...mapGetters({
      printMode: PRINT_GET_PRINT_MODE,
      tableMode: PRINT_GET_TABLE_MODE
    })
  },

  methods: {
    ...mapActions({
      enableLoading: ENABLE_LOADING,
      disableLoading: DISABLE_LOADING
    }),

    removeSeriesDisabled (series) {
      return this.tableMode
          ? series.filter(x => typeof(x.enableOnReport) === 'undefined' || x.enableOnReport)
          : series.filter(x => typeof(x.enableOnChart) === 'undefined' || x.enableOnChart);
    },

    getWidgetConfig () {
      if (this.noOverlapping) {
        this.labelVisual = new LabelVisualOverlapping(this.height, 1000);
      }

      let seriesLabel = {
        visible: false,
        font: `bold ${this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`,
        format: this.format,
        color: '#212121',
        margin: { bottom: -1, top: -1 }
      };

      let seriesClick = () => {};

      if (this.label) {
        seriesLabel = _.assign({}, seriesLabel, this.label);
      }

      _.forEach(this.removeSeriesDisabled(this.series), (s) => {
        if (s.labels) {
          s.labels = _.assign({}, seriesLabel, s.labels);
        }
      });

      let title;
      if (this.title) {
        title = _.assign({}, seriesLabel, {
          visible: !!this.title,
          text: this.title,
          position: 'top'
        });
      }

      let valueAxis;
      if (_.isArray(this.valueAxis)) {
        valueAxis = _.map(this.valueAxis, v =>
          _.assign({}, {
            majorGridLines: {
              visible: false
            },
            labels: {
              font: `bold ${this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`,
              format: this.format
            }
          }, v)
        );
      } else {
        valueAxis = _.assign({}, {
          majorGridLines: {
            visible: false
          },
          labels: {
            font: `bold ${this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`,
            format: this.format
          }
        }, this.valueAxis);
      }

      let categoryAxis;
      if (this.category || this.categoryAxis) {
        categoryAxis = _.assign({}, {
          field: this.category.field,
          majorGridLines: {
            visible: false
          },
          labels: _.assign({}, {
            visible: true,
            rotation: 'auto',
            font: `bold ${this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`
          }, this.category.labels)
        }, this.categoryAxis);
      }

      const render = () => {
        if (this.labelVisual) this.labelVisual.resetCreator();
      };

      const dataBound = () => {
        if (this.labelVisual) this.labelVisual.resetCreator();
      };

      const config = {
        render,
        dataBound,
        renderAs: this.renderAs,
        theme: 'bootstrap',
        transitions: false,
        categoryAxis: undefined,
        dataSource: undefined,
        title,
        chartArea: _.assign({}, {
          height: this.height,
          background: 'transparent',
          margin: {
            top: 5,
            left: 20,
            bottom: 5,
            right: 20
          }
        }, this.chartArea),
        legend: _.assign({}, {
          position: 'bottom',
          labels: {
            margin: {
              right: 10
            }
          },
          margin: {
            top: 5,
            bottom: 0
          },
          item: {
            visual: (e) => {
              const textWidth = (() => {
                if (!$.fn.fakeEl) $.fn.fakeEl = $('<span>').hide().appendTo(document.body);
                $.fn.fakeEl.text(e.series.name.trim()).css('font', `bold ${this.legend.fontSize || this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`);
                return Math.round($.fn.fakeEl.width());
              })();

              const borderColor = (e.series.border || e.series).color;
              const color = e.series.markers ? (_.some(['marker'], t => this.legend.type === t) ? e.series.markers.background || e.series.color : e.options.markers.background) : e.series.color;
              const labelColor = (e.options.labels || e.series).color;
              const rect = new kendo.geometry.Rect([0, 0], [textWidth + 100, 50]);
              const draw = kendo.drawing;
              const layout = new draw.Layout(rect, {
                spacing: 5,
                alignItems: 'center'
              });

              const style = {
                fill: {
                  color: color
                },
                stroke: {
                  width: 2,
                  color: borderColor
                }
              };

              const marker = new draw.Group();

              switch (e.series.markers && e.series.markers.typeOverlap) {
                case 'dash':
                  marker.append(new draw.Path({
                    ...style,
                    stroke: { ...style.stroke, dashType: 'dash' }
                  }).moveTo(13, 0).lineTo(-13, 0));
                  break;
                case 'line':
                  marker.append(new draw.Path(style).moveTo(13, 0).lineTo(-13, 0));
                  break;
              }

              const markerType = (e.series.markers && this.legend.type === 'marker') ? e.series.markers.type || 'rect' : this.legend.type;
              switch (markerType) {
                case 'line':
                  marker.append(new draw.Path(style).moveTo(11, 0).lineTo(-11, 0));
                  break;
                case 'circle':
                  marker.append(new draw.Circle(new kendo.geometry.Circle([0, 0], 5), style));
                  break;
                case 'triangle':
                  marker.append(new draw.Path(style).moveTo(0, -5).lineTo(5, 5).lineTo(-5, 5).close());
                  break;
                default:
                  if (e.series.markers && e.series.markers.rotation) {
                    marker.append(new draw.Path(style).moveTo(0, 6).lineTo(6, 0).lineTo(0, -6).lineTo(-6, 0).close());
                  } else {
                    marker.append(new draw.Path(style).moveTo(5, 5).lineTo(5, -5).lineTo(-5, -5).lineTo(-5, 5).close());
                  }
              }

              const label = new draw.Text(`${e.series.name.trim()}`, [0, 0], {
                fill: {
                  color: labelColor
                },
                font: `bold ${this.legend.fontSize || this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`
              });

              if (this.legend.spacing > 0 || this.legend.spacing === undefined) {
                const group = new draw.Group();
                const rectGeometry = new kendo.geometry.Rect([0, 0], [textWidth + (this.legend.spacing || 20), 1]);
                const space = new draw.Rect(rectGeometry);
                group.append(label, space);
                layout.append(marker, group);
              } else {
                layout.append(marker, label);
              }
              layout.reflow();

              return layout;
            }
          }
        }, this.legend),
        seriesDefaults: {
          type: this.type,
          stack: this.stack,
          gap: this.gap,
          spacing: 0,
          labels: seriesLabel || false,
          missingValues: 'gap',
          tooltip: {
            visible: true,
            format: this.format
          },
          area: {
            line: {
              style: 'smooth'
            }
          }
        },
        series: this.removeSeriesDisabled(this.series),
        seriesClick: this.seriesClick,
        seriesColors: this.colors,
        valueAxis,
        tooltip: {
          visible: true
        }
      };

      if (categoryAxis) {
        config.categoryAxis = categoryAxis;
      }

      if (this.dataSource) {
        config.dataSource = {
          data: this.dataSource
        };
      }

      return config;
    },

    exportAsExcel () {
      const rows = [{
        cells: _.concat([{ value: this.category.name }], _.map(this.dataSource, (val, key) => ({
          value: val[this.category.field]
        })))
      }];
      _.forEach(this.removeSeriesDisabled(this.series), (val, key) =>
        rows.push({
          cells: _.concat([{ value: val.name }], _.map(this.dataSource, (v, k) => ({
            value: v[val.field]
          })))
        })
      );
      const workbook = new kendo.ooxml.Workbook({
        sheets: [
          {
            columns: _.map(_.range(0, this.dataSource.length + 1), () => ({ autoWidth: true })),
            rows
          }
        ]
      });
      kendo.saveAs({
        dataURI: workbook.toDataURL(),
        fileName: `${this.formatDate(new Date(), 'YYYYMMDD-hhmmss')}_gig-chart-table.xlsx`
      });
    },

    exportChart () {
      this.widget.exportSVG().done((data) => {
        kendo.saveAs({
          dataURI: data,
          fileName: `${this.formatDate(new Date(), 'YYYYMMDD-hhmmss')}_gig-chart-image.svg`
        });
      });
    },

    zoom () {
      if (!this.zoomActive) {
        this.zoomIn = !this.zoomIn;
        setTimeout(() => {
          this.zoomActive = !this.zoomActive;
          this.generateImage($(this.$refs.chartContainer).height(), $(this.$refs.chartContainer).width());
        }, 0);
      } else {
        this.zoomActive = !this.zoomActive;
        setTimeout(() => {
          this.zoomIn = !this.zoomIn;
          this.generateImage();
        }, 300);
      }
    },

    refreshChart () {
      if (this.printMode || !this.widget)
        return;

      if (this.widget.element.height() !== this.height) {
        const chartArea = Object.assign({}, this.config.chartArea, {
          height: this.zoomIn ? 500 : this.height
        });
        this.setOptions({ chartArea });
      } else if (this.widget.element.width() !== this.widget._width) {
        this.widget.refresh();
        this.widget._width = this.widget.element.width();
      }
    },

    generateImage (height = this.printSize.height, width = this.printSize.width) {
      const reset = () => {
        clearTimeout(this.generatingImageTimeout);
        this.generatingImageTimeout = undefined;
        this.isGeneratingImage = false;
        this.disableLoading();
      };

      if (this.isGeneratingImage) {
        reset();
      }

      if (this.widget) {
        this.isGeneratingImage = true;
        this.enableLoading();

        this.generatingImageTimeout = setTimeout(() => {
          if (this.widget) {
            this.widget.exportImage({ height, width }).done((image) => {
              this.image = image;
              reset();
            });
          }
        }, 300);
      }
    },

    setOptions (options) {
      const reset = () => {
        clearTimeout(this.updatingOptionsTimeout);
        this.updatingOptionsTimeout = undefined;
        this.disableLoading();
      };

      if (this.updatingOptions) {
        reset();
      }

      this.updatingOptions = _.assign({}, this.updatingOptions, options);
      this.enableLoading();

      this.updatingOptionsTimeout = setTimeout(() => {
        if (this.widget) {
          this.widget.setOptions(this.updatingOptions);
          this.generateImage();
        }

        delete this.updatingOptions;

        reset();
      }, 500);
    },

    subscribeToReRenderOnPrint() {
      if ('matchMedia' in window) {
        window.matchMedia('print').addListener((media) => this.refreshChart());
        window.matchMedia('screen').addListener((media) => this.refreshChart());
      } else {
        window.onbeforeprint = this.refreshChart;
        window.onafterprint = this.refreshChart;
      }
    },

    getFieldSymbols() {
      return  this.removeSeriesDisabled(this.series)
          .filter(x => typeof(x.useSymbol) !== 'undefined')
          .map(x => ({ field: x.field, useSymbol: x.useSymbol }));
    },

    dataIsValid(data) {
      return typeof(data) == 'string';
    },

    getDataSourceWithSymbol(data, symbol) {
      return (data === undefined) ? `0${symbol}` : `${data}${symbol}`;
    },

    getDataSource(dataSource) {
      const fieldSymbols = this.getFieldSymbols();

      dataSource.map(data => {
        fieldSymbols.forEach(symbol => {
          if (this.dataIsValid(data[symbol.field])) {
            return;
          }

          data[symbol.field] = this.getDataSourceWithSymbol(data[symbol.field], symbol.useSymbol);
        });
      })

      return dataSource;
    }
  },

  created () {
    this.config = this.getWidgetConfig();
    this.enableLoading();
    this.subscribeToReRenderOnPrint();
  },

  mounted () {
    this.widget = new kendo.dataviz.ui.Chart(this.$refs.widget, this.config);
    this.generateImage();
    setTimeout(() => {
      this.disableLoading();
    }, 1000);
  },

  destroyed () {
    if (this.widget) {
      this.widget.destroy();
      this.widget = null;
    }
  },

  watch: {
    dataSource (val) {
      if (this.widget) {
        if (this.label) {
          const seriesLabel = this.label;
          seriesLabel.font = `bold ${this.fontSize}px 'Roboto Condensed', 'Helvetica Neue', Arial, Helvetica, sans-serif`;
          seriesLabel.format = this.format;
          this.config.seriesDefaults.labels = seriesLabel;
          this.setOptions(this.config);
        }

        const reset = () => {
          clearTimeout(this.updatingDataSourceTimeout);
          this.updatingDataSourceTimeout = undefined;
        };

        if (this.updatingDataSource) {
          reset();
        }

        this.updatingDataSource = val;

        this.updatingDataSourceTimeout = setTimeout(() => {
          this.widget.setDataSource(new kendo.data.DataSource({ data: this.updatingDataSource }));
          this.generateImage();
          delete this.updatingDataSource;

          reset();
        }, 1000);
      }
    },

    category (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal) && this.widget) {
        const categoryAxis = Object.assign({}, (this.config || {}).categoryAxis, {
          field: newVal.field
        });
        this.setOptions({ categoryAxis });
      }
    },

    series (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal) && this.widget) {
        if (this.labelVisual) {
          _.each(_.filter(newVal, series => series.labels), series => {
            series.labels.visual = this.labelVisual.create;
          });
        }
        this.setOptions({ series: this.removeSeriesDisabled(newVal) });
      }
    },

    valueAxis (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal) && this.widget) {
        this.setOptions({ valueAxis: newVal });
      }
    },

    legend (newVal, oldVal) {
      if (!_.isEqual(newVal, oldVal) && this.widget) {
        this.setOptions({ legend: newVal });
      }
    },

    tableMode (val) {
      if (val) {
        setTimeout(() => {
          this.table = new kendo.ui.Grid(this.$refs.table, {
            dataSource: {
              data: this.getDataSource(this.dataSource)
            },
            columns: _.concat([{
              field: this.category.field,
              title: this.category.name,
            }], _.map(this.removeSeriesDisabled(this.series), (val, key) => ({
              field: val.field,
              title: val.name
            })))
          });
        }, 100);
      } else {
        if (this.table) {
          this.table.destroy();
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .ui.buttons {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
  }

  .fullscreen {
    position: fixed !important;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: 2;
    background-color: rgba(0, 0, 0, 0.4);
    padding: 10%;

    .image {
      background-color: white;
    }
  }

  .fullscreen-button {
    position: fixed !important;
    top: 12% !important;
    right: 10% !important;
    background-color: white;
  }

  @media only print {
    .ui.basic.tiny.buttons.actions{
      display: none;
    }
  }
</style>
